import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Category, DeducedProduct, ProductFull } from '../types/catalog';
import { fetchCategories, fetchDeduced, fetchProduct, fetchProductByArticle } from './api';

const mergeCategoryTrees = (a: Category[], b: Category[]) => {
    const newCategories = a.slice();
    for (const catB of b) {
        const idx = newCategories.findIndex((catA) => catA.slug === catB.slug);
        if (idx === -1) {
            newCategories.push(catB);
        } else {
            const catA = newCategories[idx];
            newCategories[idx] = { ...catA, ...catB, slugPath: catA.slugPath };
            if (catB.children) {
                if (catA.children) {
                    newCategories[idx].children = mergeCategoryTrees(catA.children, catB.children);
                }
            } else {
                newCategories[idx].children = catA.children;
            }
        }
    }
    return newCategories;
};
const treeify = (slugs: string[], children: Category[]) => {
    if (!children.length) {
        return [];
    }
    const tree: Category[] = [];
    let currentTree = { children: tree };
    for (const slug of slugs) {
        const elem = {
            slug,
            children: [],
        };
        currentTree.children.push(elem as any as Category);
        currentTree = elem;
    }
    currentTree.children = children;
    return tree;
};
const mapCategories = (cats: Category[], path: string[] = []) => {
    for (const cat of cats) {
        cat.slugPath = [...path, cat.slug];
        if (cat.children) {
            mapCategories(cat.children, cat.slugPath);
        }
    }
    return cats;
};
export const emptyState = {
    pathname: '',
    article: null,
    categorySlugs: [],
    categories: [],
    currentCategory: null,
    productSlug: null,
    isProduct: false,
    product: null,
    isDeduced: false,
    isUnreleasedProduct: false,
    deducedProducts: [],
    fetchCategoryTree: () => Promise.resolve([]),
    fetchCategoryChildren: () => Promise.resolve([]),
};

export interface CategoryInfo {
    pathname: string;
    article: string | null;
    categorySlugs: string[];
    categories: Category[];
    isProduct: boolean;
    product: ProductFull | null;
    isDeduced: boolean;
    deducedProducts: DeducedProduct[];
    isUnreleasedProduct: boolean;
    productSlug: string | null;
    currentCategory: Category | null;
    fetchCategoryTree: (slug?: string[]) => Promise<any>;
    fetchCategoryChildren: (slug?: string[]) => Promise<any>;
}

const alreadyFetched = { tree: new Set<string>(), normal: new Set<string>() };

export function useCategories(): CategoryInfo {
    const [categories, setCategories] = useState([] as Category[]);
    const [product, setProduct] = useState(null as ProductFull | null);
    const [deducedProducts, setDeducedProducts] = useState<DeducedProduct[]>([]);
    const location = useLocation();
    const nav = useNavigate();
    const {
        categorySlugs,
        isProduct = false,
        isDeduced = false,
        productSlug = null,
        article = null,
        isUnreleasedProduct = false,
    } = useMemo(() => {
        if (location.pathname === '/search/') {
            return { categorySlugs: [] };
        }
        if (location.pathname === '/deduced/') {
            return { categorySlugs: [], isDeduced: true };
        }
        let slugParts = location.pathname.split('/').filter((s) => s);
        // для редиректа на товар по артикулу
        if (slugParts.length === 2 && slugParts[0] === 'article') {
            const article = slugParts[1];
            if (article === 'unreleased') {
                return { categorySlugs: [], isProduct: true, isUnreleasedProduct: true };
            }
            return { categorySlugs: [], article };
        }
        const isProduct = !location.pathname.endsWith('/');
        let productSlug = null;
        let categorySlugs = slugParts;
        if (isProduct) {
            productSlug = slugParts[slugParts.length - 1];
            categorySlugs = slugParts.slice(0, slugParts.length - 1);
        }
        return { categorySlugs, isProduct, productSlug };
    }, [location.pathname]);

    const fetchCategoryTree = useCallback((slugs) => {
        return fetchCategories(slugs, true).then((newCategories) =>
            setCategories((categories) => mergeCategoryTrees(categories, mapCategories(newCategories)))
        );
    }, []);
    const fetchCategoryChildren = useCallback((slugs) => {
        return fetchCategories(slugs).then((newCategories) =>
            setCategories((categories) =>
                mergeCategoryTrees(categories, mapCategories(treeify(slugs, newCategories)))
            )
        );
    }, []);

    const currentCategory = useMemo(() => {
        let currentCategories = categories;
        let category = null;

        for (const part of categorySlugs) {
            const currentCategory = currentCategories.find(({ slug }) => slug === part);
            if (currentCategory) {
                category = currentCategory;
                if (category.children) {
                    currentCategories = category.children;
                } else {
                    currentCategories = [];
                }
            } else {
                return null;
            }
        }
        return category;
    }, [categories, categorySlugs]);

    useEffect(() => {
        // скоро будет редирект и ничего делать не надо
        if (article) {
            return;
        }
        // если нет категории или мы в корне и категории еще не вытаскивались
        const path = categorySlugs.join('/');
        if (
            (!currentCategory && (categorySlugs.length || !categories.length)) ||
            (currentCategory && !currentCategory.children)
        ) {
            if (alreadyFetched.tree.has(path)) {
                console.error('Попытка повторно вытащить дерево категорий ' + path);
                return;
            }
            fetchCategoryTree(categorySlugs);
            alreadyFetched.tree.add(path);
        } else if (currentCategory && currentCategory.children?.some((c) => !c.children)) {
            if (alreadyFetched.normal.has(path)) {
                console.error('Попытка повторно вытащить категорию ' + path);
                return;
            }
            fetchCategoryChildren(categorySlugs);
            alreadyFetched.normal.add(path);
        }
    }, [
        categories.length,
        currentCategory,
        categorySlugs,
        article,
        fetchCategoryTree,
        fetchCategoryChildren,
    ]);
    useEffect(() => {
        if (article) {
            fetchProductByArticle(article).then((product) => {
                if (product) {
                    nav(product.url);
                } else {
                    nav('/unknown_product');
                }
            });
        }
    }, [article, nav]);

    useEffect(() => {
        if (isDeduced && !deducedProducts.length) {
            fetchDeduced().then(setDeducedProducts);
        }
    }, [deducedProducts, isDeduced]);

    useEffect(() => {
        if (isProduct && !isUnreleasedProduct) {
            fetchProduct(location.pathname).then((product) => {
                if (product.url !== location.pathname) {
                    nav(product.url);
                } else {
                    setProduct(product);
                }
            });
        } else {
            setProduct(null);
        }
    }, [isProduct, isUnreleasedProduct, location.pathname, nav]);
    return {
        pathname: location.pathname,
        article,
        categorySlugs,
        categories,
        isProduct,
        productSlug,
        fetchCategoryTree,
        fetchCategoryChildren,
        currentCategory,
        product,
        isDeduced,
        deducedProducts,
        isUnreleasedProduct,
    };
}
